import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGoogleSignIn: () => Promise<void>;
  onEmailSignIn: (email: string, password: string) => Promise<void>;
  onEmailSignUp: (email: string, password: string) => Promise<void>;
  customHeading?: string;
  customSubtext?: string;
  onAuthComplete?: () => void;
  defaultSignIn?: boolean;
}

export function AuthModal({ isOpen, onClose, onGoogleSignIn, onEmailSignIn, onEmailSignUp, customHeading, customSubtext, onAuthComplete, defaultSignIn }: AuthModalProps) {
  const [isSignUp, setIsSignUp] = useState(!defaultSignIn);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Sync sign-in/sign-up mode when defaultSignIn prop changes
  useEffect(() => {
    if (isOpen) setIsSignUp(!defaultSignIn);
  }, [defaultSignIn, isOpen]);

  // Track auth modal opened event
  useEffect(() => {
    if (isOpen) {
      import('../lib/analytics').then(({ trackAuthModalOpened }) =>
        trackAuthModalOpened(isSignUp ? 'signup' : 'signin')
      );
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isSignUp) await onEmailSignUp(email, password);
      else await onEmailSignIn(email, password);
      onClose();
      if (onAuthComplete) {
        onAuthComplete();
      } else {
        navigate('/app');
      }
    } catch (err) {
      if (err instanceof Error && err.message === '__EMAIL_CONFIRMATION_REQUIRED__') {
        setError(`We sent a confirmation link to ${email}. Check your inbox (and spam folder), then come back and sign in.`);
        setIsSignUp(false);
      } else {
        setError(err instanceof Error ? err.message : 'We couldn\u0027t sign you in. Check your email and password, then try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-cream rounded-2xl shadow-2xl w-full max-w-sm p-8 animate-scale-in scroll-mt-32">
        <button onClick={onClose} className="absolute top-4 right-4 text-ink-faint hover:text-ink text-lg">&times;</button>

        <h2 className="font-sans text-2xl text-center text-ink mb-1" style={{ fontWeight: 600 }}>
          {customHeading ? customHeading : isSignUp ? 'Test your photo free' : 'Welcome back'}
        </h2>
        <p className="text-center text-xs text-ink-faint mb-6" style={{ fontWeight: 300 }}>
          {customSubtext ? customSubtext : isSignUp ? '3 free credits. No card required.' : 'Sign in to continue.'}
        </p>

        <button
          onClick={async () => {
            setError('');
            try {
              await onGoogleSignIn();
              onClose();
              if (onAuthComplete) {
                onAuthComplete();
              }
            } catch {
              setError('Google sign-in didn\u0027t go through. Please try again or use email instead.');
            }
          }}
          className="w-full py-3 px-4 bg-surface border border-surface-border text-ink font-sans text-sm rounded-lg hover:bg-cream-warm hover:border-ink/10 transition-all duration-300 flex items-center justify-center gap-3"
          style={{ fontWeight: 400 }}
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
          </svg>
          Continue with Google
        </button>

        <div className="flex items-center gap-4 my-6">
          <div className="flex-1 h-px bg-surface-border" />
          <span className="text-[11px] text-ink-faint font-sans">or</span>
          <div className="flex-1 h-px bg-surface-border" />
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label htmlFor="modal-email" className="block text-[11px] font-sans text-ink-faint mb-1 tracking-wider uppercase">Email</label>
            <input id="modal-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="you@example.com" className="input text-sm" />
          </div>
          <div>
            <label htmlFor="modal-password" className="block text-[11px] font-sans text-ink-faint mb-1 tracking-wider uppercase">Password</label>
            <input id="modal-password" type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={6} placeholder={isSignUp ? '6+ characters' : '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'} className="input text-sm" />
          </div>
          {!isSignUp && (
            <div className="text-right -mt-1">
              <button type="button" onClick={() => { onClose(); navigate('/forgot-password'); }} className="text-[11px] text-ink-faint hover:text-rose transition-colors font-sans">
                Forgot password?
              </button>
            </div>
          )}
          {error && <div className="text-xs text-rose bg-rose-faint p-2.5 rounded-lg font-sans">{error}</div>}
          <button type="submit" disabled={loading} className="btn-primary w-full text-sm">
            {loading ? 'Please wait...' : isSignUp ? 'Create account' : 'Sign in'}
          </button>
        </form>

        <p className="text-center mt-4 text-xs text-ink-faint font-sans">
          {isSignUp ? 'Already have an account?' : 'Don\u0027t have an account?'}{' '}
          <button onClick={() => { setIsSignUp(!isSignUp); setError(''); }} className="text-rose hover:underline font-medium">
            {isSignUp ? 'Sign in' : 'Sign up'}
          </button>
        </p>
      </div>
    </div>
  );
}
